import { useMemo, useState } from 'react';
import { Play, RotateCcw } from 'lucide-react';
import { Code } from './CodeBlock';
import { TEST_CARDS, TEST_VPAS } from '../data/api';

/* ==========================================================================
 *  Interactive sandbox
 *
 *  Runs the real fee arithmetic in the browser — same basis-point rates, same
 *  HALF-UP rounding, same integer-minor-unit discipline as the server. A reader
 *  can therefore check their own fee expectations here before writing a line of
 *  code, and the numbers will match the API to the paisa.
 *
 *  Nothing here calls the network. It is a simulator of the documented sandbox,
 *  not a proxy to it, so it works from a static docs deployment.
 * ========================================================================== */

const METHOD_RATES: Record<string, { bps: number; flatMinor: bigint; label: string }> = {
  UPI_INTENT: { bps: 200, flatMinor: 0n, label: 'UPI Intent' },
  UPI: { bps: 200, flatMinor: 0n, label: 'UPI' },
  CREDIT_CARD: { bps: 295, flatMinor: 0n, label: 'Credit Card' },
  DEBIT_CARD: { bps: 180, flatMinor: 0n, label: 'Debit Card' },
  NET_BANKING: { bps: 0, flatMinor: 1200n, label: 'Net Banking' },
  WALLET: { bps: 250, flatMinor: 0n, label: 'Wallet' },
};

const GST_BPS = 1800n;

/** HALF-UP, identical to backend/src/utils/money.ts. */
function divRoundHalfUp(numerator: bigint, denominator: bigint): bigint {
  return (numerator * 2n + denominator) / (denominator * 2n);
}

/** Paisa to the rupee decimal string the API sends. */
const rupees = (minor: bigint) => `${minor / 100n}.${String(minor % 100n).padStart(2, '0')}`;

function formatInr(minor: bigint): string {
  const whole = (minor / 100n).toString().replace(/(\d)(?=(\d\d)+\d$)/g, '$1,');
  return `₹${whole}.${(minor % 100n).toString().padStart(2, '0')}`;
}

export function Sandbox() {
  const [amountRupees, setAmountRupees] = useState('1499.50');
  const [method, setMethod] = useState('UPI_INTENT');
  const [instrument, setInstrument] = useState('success@riskpay');
  const [ran, setRan] = useState(false);

  const isUpi = method.startsWith('UPI');
  const instruments = isUpi
    ? TEST_VPAS.map((v) => ({ value: v.vpa, label: `${v.vpa} — ${v.outcome}` }))
    : TEST_CARDS.map((c) => ({ value: c.number, label: `${c.number} — ${c.outcome}` }));

  const amountMinor = useMemo(() => {
    const match = /^(\d+)(?:\.(\d{1,2}))?$/.exec(amountRupees.trim());
    if (!match) return null;
    return BigInt(match[1]!) * 100n + BigInt((match[2] ?? '0').padEnd(2, '0'));
  }, [amountRupees]);

  const outcome = useMemo(() => {
    if (isUpi) return TEST_VPAS.find((v) => v.vpa === instrument) ?? TEST_VPAS[0]!;
    const card = TEST_CARDS.find((c) => c.number === instrument) ?? TEST_CARDS[0]!;
    return { outcome: card.outcome, failureCode: card.failureCode, description: card.description };
  }, [isUpi, instrument]);

  const fees = useMemo(() => {
    if (!amountMinor || amountMinor <= 0n) return null;
    const rate = METHOD_RATES[method]!;
    const mdr = divRoundHalfUp(amountMinor * BigInt(rate.bps), 10_000n) + rate.flatMinor;
    const gst = divRoundHalfUp(mdr * GST_BPS, 10_000n);
    return { mdr, gst, net: amountMinor - mdr - gst, bps: rate.bps, flat: rate.flatMinor };
  }, [amountMinor, method]);

  const request = useMemo(() => {
    const body: Record<string, unknown> = {
      amount: amountRupees.trim(),
      currency: 'INR',
      payment_method: method,
      merchant_order_id: 'order_4821',
    };
    if (isUpi) body.customer = { vpa: instrument };
    else body.card = { number: instrument.replace(/\s/g, ''), expiry_month: 12, expiry_year: 2030, cvv: '123' };
    return JSON.stringify(body, null, 2);
  }, [amountRupees, method, instrument, isUpi]);

  const response = useMemo(() => {
    if (!fees || !amountMinor) return '{ "error": "Enter a valid amount" }';

    const succeeded = outcome.outcome === 'SUCCESS';
    const body: Record<string, unknown> = {
      id: 'pay_R7KQ2M4X9B1T',
      object: 'payment',
      status: succeeded ? 'success' : outcome.outcome === 'PENDING' ? 'pending' : 'failed',
      mode: 'test',
      amount: rupees(amountMinor),
      amount_formatted: formatInr(amountMinor),
      currency: 'INR',
      fee: { amount: rupees(fees.mdr + fees.gst), currency: 'INR', formatted: formatInr(fees.mdr + fees.gst) },
      net_amount: { amount: rupees(fees.net), currency: 'INR', formatted: formatInr(fees.net) },
      payment_method: method.toLowerCase(),
      merchant_order_id: 'order_4821',
    };

    if (isUpi) body.upi = { vpa: instrument, rrn: succeeded ? '418377210094' : null };
    else {
      const card = TEST_CARDS.find((c) => c.number === instrument);
      body.card = { network: card?.network ?? 'Visa', last4: instrument.replace(/\s/g, '').slice(-4) };
    }

    body.failure = succeeded
      ? null
      : { code: outcome.failureCode ?? 'PAYMENT_DECLINED', message: outcome.description };
    body.settled = false;
    body.completed_at = succeeded ? '2026-08-28T09:16:04.902Z' : null;

    return JSON.stringify(body, null, 2);
  }, [fees, amountMinor, outcome, method, instrument, isUpi]);

  return (
    <div className="panel overflow-hidden">
      <div className="flex items-center justify-between border-b border-line bg-surface-overlay px-4 py-2.5">
        <span className="text-2xs font-semibold uppercase tracking-wider text-content-subtle">Interactive sandbox</span>
        <button
          type="button"
          onClick={() => {
            setAmountRupees('1499.50');
            setMethod('UPI_INTENT');
            setInstrument('success@riskpay');
            setRan(false);
          }}
          className="btn btn-ghost h-7 gap-1.5 px-2 text-2xs"
        >
          <RotateCcw className="h-3 w-3" aria-hidden="true" />
          Reset
        </button>
      </div>

      <div className="grid gap-3 px-4 py-4 sm:grid-cols-2">
        <div>
          <label htmlFor="sbx-amount" className="mb-1.5 block text-2xs uppercase tracking-wider text-content-subtle">
            Amount (₹)
          </label>
          <input
            id="sbx-amount"
            value={amountRupees}
            onChange={(e) => setAmountRupees(e.target.value)}
            inputMode="decimal"
            className={`input numeral ${amountMinor === null ? 'border-danger' : ''}`}
            aria-invalid={amountMinor === null}
          />
          {amountMinor === null && (
            <p className="mt-1.5 text-2xs text-danger">Use up to two decimal places, e.g. 1499.50</p>
          )}
        </div>

        <div>
          <label htmlFor="sbx-method" className="mb-1.5 block text-2xs uppercase tracking-wider text-content-subtle">
            Payment method
          </label>
          <select
            id="sbx-method"
            value={method}
            onChange={(e) => {
              const next = e.target.value;
              setMethod(next);
              setInstrument(next.startsWith('UPI') ? 'success@riskpay' : '4242 4242 4242 4242');
            }}
            className="input"
          >
            {Object.entries(METHOD_RATES).map(([value, rate]) => (
              <option key={value} value={value}>
                {rate.label}
              </option>
            ))}
          </select>
        </div>

        <div className="sm:col-span-2">
          <label htmlFor="sbx-instrument" className="mb-1.5 block text-2xs uppercase tracking-wider text-content-subtle">
            {isUpi ? 'Test VPA' : 'Test card'}
          </label>
          <select
            id="sbx-instrument"
            value={instrument}
            onChange={(e) => setInstrument(e.target.value)}
            className="input numeral"
          >
            {instruments.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* ---- Fee breakdown, computed with the server's own arithmetic ---- */}
      {fees && amountMinor !== null && (
        <dl className="grid grid-cols-2 gap-px border-y border-line bg-line sm:grid-cols-4">
          {[
            ['Gross', formatInr(amountMinor)],
            [`MDR (${(fees.bps / 100).toFixed(2)}%${fees.flat > 0n ? ` + ${formatInr(fees.flat)}` : ''})`, `−${formatInr(fees.mdr)}`],
            ['GST (18%)', `−${formatInr(fees.gst)}`],
            ['Net to you', formatInr(fees.net)],
          ].map(([label, value], i) => (
            <div key={label} className="bg-surface-raised px-4 py-3">
              <dt className="text-2xs uppercase tracking-wider text-content-subtle">{label}</dt>
              <dd className={`numeral mt-1 text-xs ${i === 3 ? 'font-semibold text-brand-400' : 'text-content'}`}>
                {value}
              </dd>
            </div>
          ))}
        </dl>
      )}

      <div className="border-b border-line">
        <div className="flex items-center justify-between px-4 py-2">
          <span className="text-2xs uppercase tracking-wider text-content-faint">Request body</span>
          <button type="button" onClick={() => setRan(true)} className="btn btn-primary h-7 gap-1.5 px-2.5 text-2xs">
            <Play className="h-3 w-3" aria-hidden="true" />
            Run in sandbox
          </button>
        </div>
        <Code source={request} json />
      </div>

      <div>
        <div className="flex items-center justify-between px-4 py-2">
          <span className="text-2xs uppercase tracking-wider text-content-faint">
            {ran ? '200 OK · simulated response' : 'Expected response'}
          </span>
          <span className={`badge ${outcome.outcome === 'SUCCESS' ? 'badge-success' : outcome.outcome === 'PENDING' ? 'badge-warning' : 'badge-danger'}`}>
            {outcome.outcome}
          </span>
        </div>
        <Code source={response} json />
      </div>

      <p className="border-t border-line bg-surface-inset px-4 py-3 text-2xs leading-relaxed text-content-faint">
        Fees here are computed with the same basis-point rates and HALF-UP rounding the API uses, so these figures match
        a real sandbox call to the paisa. Your live rates are whatever is configured on your account.
      </p>
    </div>
  );
}
